import { Navigate } from "react-router";
import { useAuth } from "@/context/useAuth";
import { usePermissions } from "@/context/PermissionContext";
import { useEffect } from "react";
import { AUTH_CONSTANTS } from '@/constants/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
  menuId?: string;
}

export default function ProtectedRoute({ children, roles, menuId }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();


  // Safari/Back-Forward-Cache Abfang: wenn Seite aus BFCache kommt, Auth erneut prüfen
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // Seite wurde aus dem BFCache geladen
        if (!isAuthenticated) {
          console.log('BFCache restore detected while unauthenticated -> redirect to /signin');
          window.location.replace(AUTH_CONSTANTS.ROUTES.SIGNIN);
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!isAuthenticated) {
          console.log('Tab became visible while unauthenticated -> redirect to /signin');
          window.location.replace(AUTH_CONSTANTS.ROUTES.SIGNIN);
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  // NUR ein einfacher Loading/Redirect Check - KEINE History-Manipulation hier!
  
  // Zeige Loading während der Authentifizierung oder Permission-Loading
  if (isLoading || permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Wenn nicht authentifiziert, zur Login-Seite weiterleiten
  if (!isAuthenticated) {
    console.log('Not authenticated, redirecting to signin');
    return <Navigate to={AUTH_CONSTANTS.ROUTES.SIGNIN} replace />;
  }

  // Permission Check
  if (menuId && !hasPermission(menuId)) {
    console.log(`Access denied: No permission for menu '${menuId}'`);
    return <Navigate to={AUTH_CONSTANTS.ROUTES.HOME} replace />;
  }

  if (roles && user) {
    // Bestimme User-Rolle mit Priorität: admin > director > manager > employee
    let userRole: string;
    if ((user as any)?.is_superuser) {
      userRole = 'admin';
    } else {
      const branchRoles = (user as any)?.assigned_branches?.map((b: any) => b.role) || [];
      if (branchRoles.includes('director')) {
        userRole = 'director';
      } else if (branchRoles.includes('manager')) {
        userRole = 'manager';
      } else if ((user as any)?.is_staff) {
        userRole = 'employee';
      } else {
        userRole = 'user';
      }
    }
    
    if (!roles.includes(userRole)) {
      console.log(`Access denied: User role '${userRole}' not in allowed roles:`, roles);
      return <Navigate to={AUTH_CONSTANTS.ROUTES.HOME} replace />;
    }
  }

  // Wenn authentifiziert, zeige die geschützte Komponente
  return <>{children}</>;
}
